import "./EventList.css";

import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { ErrorBoundary } from "../../../errors/ErrorBoundary";
import Modal, { ModalSize } from "../../../misc/components/modal";
import {
    CloseEventListDialogAction,
    JumpToTimePointAction,
    RemovePeriodEventAction,
    RemovePointEventAction,
    SetDuplicateVerifiedAction
} from "../actions";
import { AnalyseState } from "../interfaces/state";
import { EventListTable } from "./EventListTable";
import { EventTotals } from "./EventTotals";
import { Query } from '../../../misc/components/queryTable/Query';
import { useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";
import { Form } from "informedv4";
import { PeaksInstance, Point, Segment } from "peaks.js";
import { QueryOrder } from "../../../misc/api/apiTypes";

export interface EventListProps {
    isVisible: boolean;
    peaksJs: PeaksInstance;
    selectedSegment: number;
    dispatch?: Dispatch<AnalyseState>;
}

export const EventList = (props: EventListProps) => {
    const [periodTableQuery, setPeriodTableQuery] = React.useState<Query<any>>();
    const [pointTableQuery, setPointTableQuery] = React.useState<Query<any>>();
    const [pointSortOrder, setPointSortOrder] = React.useState<QueryOrder | null>(null);
    const [periodSortOrder, setPeriodSortOrder] = React.useState<QueryOrder | null>(null);
    const [points, setPoints] = React.useState<Point[]>();
    const [periods, setPeriods] = React.useState<Segment[]>();

    const legacyClient = useWebPortalLegacyClient();

    React.useEffect(() => {
        setPoints(props.peaksJs.points.getPoints());
        setPeriods(props.peaksJs.segments.getSegments());

        const refreshPoints = function() {
            setPoints([...props.peaksJs.points.getPoints()]);
        };

        const refreshPeriods = function() {
            setPeriods([...props.peaksJs.segments.getSegments()]);
        };

        props.peaksJs.on('points.add', refreshPoints);
        props.peaksJs.on('points.update', refreshPoints);
        props.peaksJs.on('points.remove', refreshPoints);
        props.peaksJs.on('segments.add', refreshPeriods);
        props.peaksJs.on('segments.update', refreshPeriods);
        props.peaksJs.on('segments.remove', refreshPeriods);

        return () => {
            props.peaksJs.off('points.add', refreshPoints);
            props.peaksJs.off('points.update', refreshPoints);
            props.peaksJs.off('points.remove', refreshPoints);
            props.peaksJs.off('segments.add', refreshPeriods);
            props.peaksJs.off('segments.update', refreshPeriods);
            props.peaksJs.off('segments.remove', refreshPeriods);
        };
    }, [props.peaksJs]);

    if (props.isVisible) {

        return (
            <Form validateOn="submit">
            <Modal
                show={true}
                size={ModalSize.Large}
                header="Event List"
                closeModalHandler={() =>
                    props.dispatch(CloseEventListDialogAction.create())
                }>
                <div className="modal-body">
                    <div className="row">
                        <div className="col-md-12">
                            <EventTotals
                                    pointEvents={points}
                                    periodEvents={periods}
                                    isDisputedView={false}

                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <h3 className="text-primary">
                                Individual Period Events
                            </h3>
                            <ErrorBoundary>
                                <EventListTable
                                    key="PeriodsEventsTable"
                                    startTimeColumnName="Waveform Start Time"
                                    timestampColumn="startTime"
                                    withEndTime={true}
                                    events={periods}
                                    onJumpToEventClick={timepoint => {
                                        props.dispatch(
                                            JumpToTimePointAction.create(
                                                timepoint
                                            )
                                        );
                                        props.dispatch(
                                            CloseEventListDialogAction.create()
                                        );
                                    }}
                                    onDeleteEventClick={id =>
                                        props.dispatch(
                                            RemovePeriodEventAction.create(
                                                legacyClient,
                                                id
                                            )
                                        )
                                    }
                                    query={periodTableQuery}
                                    selectedSegment={props.selectedSegment}
                                    sortOrder={periodSortOrder}
                                    onSortChanged={setPeriodSortOrder}
                                />
                            </ErrorBoundary>
                        </div>
                        <div className="col-md-6">
                            <h3 className="text-primary">
                                Individual Point Events
                            </h3>
                                <ErrorBoundary>
                                    <EventListTable
                                        key="PointsEventsTable"
                                        startTimeColumnName="Waveform Time"
                                        timestampColumn="time"
                                        withEndTime={false}
                                        events={points}
                                        renderRawTime
                                        onJumpToEventClick={timepoint => {
                                            props.dispatch(
                                                JumpToTimePointAction.create(
                                                    timepoint
                                                )
                                            );
                                            props.dispatch(
                                                CloseEventListDialogAction.create()
                                            );
                                        }}
                                        onDeleteEventClick={id =>
                                            props.dispatch(
                                                RemovePointEventAction.create(
                                                    legacyClient,
                                                    id
                                                )
                                            )
                                        }
                                        onConfirmDuplicateThresholdEventClick={async id => {
                                            props.dispatch(SetDuplicateVerifiedAction.create(id));
                                        }}
                                        query={pointTableQuery}
                                        selectedSegment={props.selectedSegment}
                                        sortOrder={pointSortOrder}
                                        onSortChanged={setPointSortOrder}
                                     />
                            </ErrorBoundary>
                        </div>
                    </div>
                </div>
                <div className="modal-footer">
                    <button
                        className="btn btn-primary"
                        onClick={() =>
                            props.dispatch(
                                CloseEventListDialogAction.create()
                            )
                        }>
                        Close
                    </button>
                </div>
                </Modal>
            </Form>
        );
    } else {
        return null;
    }
}

function mapStateToProps(state: AnalyseState) {
    return {
        isVisible: state.core.isEventListOpen,
        peaksJs: state.core.peaksJs,
        selectedSegment: state.core.selectedSegment
    };
}

export default connect<EventListProps, any>(mapStateToProps)(EventList);
